/**
 * Depth Scroll — Zoom scroll effect for featured works
 *
 * Pattern : scroll listener (passive) → RAF one-shot → lerp
 * Un seul RAF actif à la fois, s'arrête quand le lerp est stabilisé.
 *
 * Snap scroll : détecte la fin du scroll et ramène window.scrollY
 * sur le slot le plus proche. Le lerp visuel existant n'est pas modifié.
 *
 * @module components/zscroll
 */ 

// ==========================================================================
// STATE
// ==========================================================================

let _container    = null;
let _items        = [];
let _config       = {};
let _rafId        = null;
let _scrollHandler = null;
let _resizeHandler = null;
let _resizeTimer = null;
let _initialized  = false;
let _snapTimer    = null;
let _isSnapping   = false;
let _scrollDir    = 0;    // 1 = vers le bas, -1 = vers le haut
let _prevScrollY  = 0;
let _dotEls       = [];
let _dotHandlers  = [];

const LERP_THRESHOLD = 0.0005; // En dessous : on considère le lerp stabilisé

// ==========================================================================
// DEFAULTS
// ==========================================================================

const DEFAULTS = {
    itemSelector:   '.z-scroll__item',

    // Entrée (avant le slot)
    scaleEnterFrom:   0.2,
    scaleEnterTo:     1.0,
    opacityEnterFrom: 0.0,
    opacityEnterTo:   1.0,

    // Sortie (après le slot)
    scaleExitFrom:    1.0,
    scaleExitTo:      3.0,
    opacityExitFrom:  1.0,
    opacityExitTo:    0.0,

    blurEnterFrom: 48,   // px — flou au début de l'entrée
    blurEnterTo:   0,    // px — net au centre
    blurExitFrom:  0,    // px — net au début de la sortie
    blurExitTo:    48,   // px — flou à la fin de la sortie

    activationZone: 0.5,
    holdZone: 0.10,
    slotSpacing:    .7,   // distance entre slots consécutifs (< 1 = chevauchement)
    lerpFactor:     0.10,

    snapEnabled:          true,
    snapDebounce:         150,  // ms après le dernier scroll avant de snapper
    snapInstantThreshold: 12,   // px — sous ce seuil : snap instantané (évite la zone d'arrivée)
    snapDeadZone:         0.02, // fraction de innerHeight — mouvement min pour avancer au slot suivant .02 très réactif - .08 plus résistant

    onActiveChange: null,
    onScroll:       null,
};

// ==========================================================================
// INTERNAL
// ==========================================================================



function _computeProgress() {
    const scrollY      = window.scrollY;
    const slotHeight   = window.innerHeight;
    const containerTop = _container.offsetTop;
    const scrollNorm   = (scrollY - containerTop) / slotHeight;

    let maxProximity = -1;
    let activeIndex  = -1;

    _items.forEach((item, i) => {
        const slotOffset = i * _config.slotSpacing;
        const delta      = scrollNorm - slotOffset;

        item.targetProgress = delta;

        const proximity = 1 - Math.min(Math.abs(delta), _config.activationZone) / _config.activationZone;
        if (proximity > maxProximity) {
            maxProximity = proximity;
            activeIndex  = i;
        }
    });

    return activeIndex;
}




function _onResize() {
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const totalHeight = ((_items.length - 1) * _config.slotSpacing + 1) * vh;

    _container.style.height = `${totalHeight}px`;
    _scheduleUpdate();
}


function debug() {
  if (!_initialized) {
    console.warn('[zScroll] debug() appelé avant init()');
    return;
  }

  const scrollY      = window.scrollY;
  const totalScroll  = document.documentElement.scrollHeight - window.innerHeight;
  const globalNorm   = totalScroll > 0 ? scrollY / totalScroll : 0;

  const containerTop    = _container.offsetTop;
  const containerHeight = _container.offsetHeight - window.innerHeight;
  const containerNorm   = containerHeight > 0
    ? Math.max(0, Math.min(1, (scrollY - containerTop) / containerHeight))
    : 0;

  console.group('[zScroll] debug');
  console.log(`scroll global normalisé    : ${globalNorm.toFixed(3)}`);
  console.log(`scroll conteneur normalisé : ${containerNorm.toFixed(3)}`);
  _items.forEach((item, i) => {
    const phase =
      item.progress < -(_config.holdZone * 0.5) ? 'enter' :
      item.progress >  (_config.holdZone * 0.5) ? 'exit'  : 'hold';
    console.log(
      `item[${i}]  delta: ${item.progress.toFixed(3).padStart(7)}  target: ${item.targetProgress.toFixed(3).padStart(7)}  phase: ${phase}  active: ${item.isActive}`
    );
  });
  console.groupEnd();
}



/**
 * Vérifie si tous les lerps sont stabilisés.
 * Permet d'arrêter le RAF quand il n'y a plus rien à animer.
 */
function _isLerpSettled() {
  return _items.every(item =>
    Math.abs(item.targetProgress - item.progress) < LERP_THRESHOLD
  );
}

function _applyStyles(activeIndex) {
  const az   = _config.activationZone;
  const half = _config.holdZone * 0.5;

  _items.forEach((item, i) => {
    item.progress += (item.targetProgress - item.progress) * _config.lerpFactor;

    const delta = item.progress;
    let scale, opacity, blur;

    if (delta < -(az + half)) {
      scale   = _config.scaleEnterFrom;
      opacity = _config.opacityEnterFrom;
      blur    = _config.blurEnterFrom;

    } else if (delta < -half) {
      const t = (delta + az + half) / az;
      const s = t * t * (3 - 2 * t);
      scale   = _config.scaleEnterFrom   + (_config.scaleEnterTo   - _config.scaleEnterFrom)   * s;
      opacity = _config.opacityEnterFrom + (_config.opacityEnterTo - _config.opacityEnterFrom) * s;
      blur    = _config.blurEnterFrom    + (_config.blurEnterTo    - _config.blurEnterFrom)    * s;

    } else if (delta <= half) {
      scale   = _config.scaleEnterTo;
      opacity = _config.opacityEnterTo;
      blur    = _config.blurEnterTo; // = 0

    } else if (delta <= az + half) {
      const t = (delta - half) / az;
      const s = t * t * (3 - 2 * t);
      scale   = _config.scaleExitFrom    + (_config.scaleExitTo    - _config.scaleExitFrom)    * s;
      opacity = _config.opacityExitFrom  + (_config.opacityExitTo  - _config.opacityExitFrom)  * s;
      blur    = _config.blurExitFrom     + (_config.blurExitTo     - _config.blurExitFrom)     * s;

    } else {
      scale   = _config.scaleExitTo;
      opacity = _config.opacityExitTo;
      blur    = _config.blurExitTo;
    }

    item.el.style.transform = `scale(${scale.toFixed(4)})`;
    item.el.style.opacity   = opacity.toFixed(4);
    item.el.style.filter    = blur > 0.01 ? `blur(${blur.toFixed(2)}px)` : 'none';
    const shouldBeActive = (i === activeIndex)
      && (delta > -(half + 0.05))
      && (delta <  (half + 0.05));

    if (shouldBeActive && !item.isActive) {
      item.isActive = true;
      item.el.classList.add('is-active');
      _dotEls.forEach((dot, di) => dot.classList.toggle('is-active', di === i));
      _config.onActiveChange?.(i, item.el);
    } else if (!shouldBeActive && item.isActive) {
      item.isActive = false;
      item.el.classList.remove('is-active');
    }
  });
}

/**
 * Boucle RAF — tourne jusqu'à stabilisation du lerp, puis s'arrête.
 */
function _tick() {
  const activeIndex = _computeProgress();
  _applyStyles(activeIndex);

  if (_config.onScroll) {
    const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
    const progress    = totalScroll > 0 ? window.scrollY / totalScroll : 0;
    _config.onScroll(progress, activeIndex);
  }

  if (_isLerpSettled()) {
    _rafId = null;
    return;
  }

  _rafId = requestAnimationFrame(_tick);
}

/**
 * Déclenche un RAF one-shot si pas déjà en cours.
 */
function _scheduleUpdate() {
  if (_rafId) return;
  _rafId = requestAnimationFrame(_tick);
}

/**
 * Calcule le slot le plus proche et y scrolle.
 * - Distance < snapInstantThreshold px → scroll instantané (évite la zone d'arrivée)
 * - Sinon → smooth scroll natif
 */
function _updateDotsVisibility() {
    const dotsNav = document.querySelector('.z-scroll__dots');
    if (!dotsNav || !_container) return;
    const scrollY      = window.scrollY;
    const containerTop = _container.offsetTop;
    const containerEnd = containerTop + _container.offsetHeight - window.innerHeight;
    const inside = scrollY >= containerTop && scrollY <= containerEnd;
    dotsNav.classList.toggle('is-visible', inside);
}

function _snapToNearest() {
    if (!_initialized || _items.length === 0) return;

    const scrollY      = window.scrollY;
    const containerTop = _container.offsetTop;
    const lastSlotY    = containerTop + (_items.length - 1) * _config.slotSpacing * window.innerHeight;

    // Hors de la zone du container → on laisse le scroll libre
    if (scrollY < containerTop || scrollY > lastSlotY) return;

    const scrollNorm  = (scrollY - containerTop) / window.innerHeight;
    const rawSlot     = scrollNorm / _config.slotSpacing;
    const nearestSlot = Math.round(rawSlot);
    const offset      = scrollNorm - nearestSlot * _config.slotSpacing; // distance au slot le plus proche (en unités vh)

    // Snap directionnel : si l'utilisateur a scrollé au-delà de la zone morte dans une direction,
    // on avance vers le slot suivant dans cette direction plutôt que de revenir en arrière.
    let directed;
    if (Math.abs(offset) >= _config.snapDeadZone && _scrollDir !== 0) {
        directed = _scrollDir > 0 ? Math.ceil(rawSlot) : Math.floor(rawSlot);
    } else {
        directed = nearestSlot;
    }
    const clamped = Math.max(0, Math.min(directed, _items.length - 1));
    const targetY  = containerTop + clamped * _config.slotSpacing * window.innerHeight;
    const distance = Math.abs(scrollY - targetY);

    if (distance < 1) return; // déjà sur le slot

    _isSnapping = true;
    const behavior = distance <= _config.snapInstantThreshold ? 'instant' : 'smooth';

    window.scrollTo({ top: targetY, behavior });

    if (behavior === 'instant') {
        _isSnapping = false;
    } else if ('onscrollend' in window) {
        window.addEventListener('scrollend', () => { _isSnapping = false; }, { once: true });
    } else {
        setTimeout(() => { _isSnapping = false; }, 800);
    }
}

// ==========================================================================
// PUBLIC API
// ==========================================================================

function init(containerId = 'zContainer', options = {}) {
    if (_initialized) destroy();

    _container = document.getElementById(containerId);

    if (!_container) {
        console.warn(`[zScroll] #${containerId} not found`);
        return;
    }

    _config = { ...DEFAULTS, ...options };

    console.log(_config.itemSelector);

    _items = Array.from(_container.querySelectorAll(_config.itemSelector)).map((el, i) => ({
        el,
        index: i,
        progress: -(_config.activationZone + _config.holdZone), // démarre hors champ
        targetProgress: 0,
        isActive: false,
    }));



    if (_items.length === 0) {
        console.warn(`[zScroll] No items: "${_config.itemSelector}"`);
        return;
    }

    const totalHeight = ((_items.length - 1) * _config.slotSpacing + 1) * window.innerHeight;
    _container.style.height = `${totalHeight}px`;

    _items.forEach(item => {
        item.el.style.willChange      = 'transform, opacity';
        item.el.style.transformOrigin = 'center center';
    });

    _prevScrollY = window.scrollY;

    // Scroll handler : passive, déclenche un RAF one-shot + snap debounce
    _scrollHandler = () => {
        const dy = window.scrollY - _prevScrollY;
        if (dy !== 0) _scrollDir = dy > 0 ? 1 : -1;
        _prevScrollY = window.scrollY;

        _updateDotsVisibility();
        _scheduleUpdate();
        if (_config.snapEnabled && !_isSnapping) {
            clearTimeout(_snapTimer);
            _snapTimer = setTimeout(_snapToNearest, _config.snapDebounce);
        }
    };
    window.addEventListener('scroll', _scrollHandler, { passive: true });

    // Resize debouncé — recalcule la hauteur du container
    _resizeHandler = () => {
        clearTimeout(_resizeTimer);
        _resizeTimer = setTimeout(_onResize, 150);
    };
    window.addEventListener('resize', _resizeHandler);

    // Dots de navigation
    _dotEls      = Array.from(document.querySelectorAll('.z-scroll__dot'));
    _dotHandlers = _dotEls.map((dot, i) => {
        const handler = () => {
            const targetY = _container.offsetTop + i * _config.slotSpacing * window.innerHeight;
            _isSnapping = true;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
            if ('onscrollend' in window) {
                window.addEventListener('scrollend', () => { _isSnapping = false; }, { once: true });
            } else {
                setTimeout(() => { _isSnapping = false; }, 800);
            }
        };
        dot.addEventListener('click', handler);
        return handler;
    });

    // Calcul initial (page pas forcément scrollée à 0 au load)
    _scheduleUpdate();
    _updateDotsVisibility();

    _initialized = true;
    console.log(`[zScroll] Init — ${_items.length} items`);
}




function destroy() {

    if (_rafId) {
        cancelAnimationFrame(_rafId);
        _rafId = null;
    }

    if (_scrollHandler) {
        window.removeEventListener('scroll', _scrollHandler);
        _scrollHandler = null;
    }

    if (_resizeHandler) {
        window.removeEventListener('resize', _resizeHandler);
        _resizeHandler = null;
    }

    if (_resizeTimer) {
        clearTimeout(_resizeTimer);
        _resizeTimer = null;
    }

    if (_snapTimer) {
        clearTimeout(_snapTimer);
        _snapTimer = null;
    }
    _isSnapping  = false;
    _scrollDir   = 0;
    _prevScrollY = 0;

    _items.forEach(item => {
        item.el.style.transform  = '';
        item.el.style.opacity    = '';
        item.el.style.willChange = '';
        item.el.classList.remove('is-active');
    });

    const dotsNav = document.querySelector('.z-scroll__dots');
    if (dotsNav) dotsNav.classList.remove('is-visible');
    _dotEls.forEach((dot, i) => {
        dot.removeEventListener('click', _dotHandlers[i]);
        dot.classList.remove('is-active');
    });
    _dotEls      = [];
    _dotHandlers = [];

    _items       = [];
    _container   = null;
    _config      = {};
    _initialized = false;

    console.log('[zScroll] Destroyed');
}

function getActiveIndex() { return _items.findIndex(i => i.isActive); }
function getItemCount()   { return _items.length; }

export { init, destroy, getActiveIndex, getItemCount };
